import { __decorate } from "tslib";
import { attr, DOM } from '@microsoft/fast-element';
import { Select, SelectPosition } from '@microsoft/fast-foundation';
import { SVG_CHEVRON_DOWN_OUTLINED } from '@getgo/chameleon-icons';
import { ChameleonElementMixin } from '../common/ChameleonElement';
import { findChild, findParent } from '../common/utils/DomUtil';
import { highRateFriendly } from '../common/utils/animationUtils';
const helperTextDefaultColor = 'text-02';
export class SelectComponent extends ChameleonElementMixin(Select) {
    constructor() {
        super();
        this.selectedValue = '';
        this.fullwidth = false;
        this.positionListbox = highRateFriendly(() => {
            var _a;
            if (this.control && this.listbox) {
                const boundingRect = this.control.getBoundingClientRect();
                this.listbox.style.position = 'fixed';
                this.listbox.style.width = `${boundingRect.width}px`;
                if (this.position === SelectPosition.above) {
                    if (this.listbox.style.top) {
                        this.listbox.style.removeProperty('top');
                    }
                    this.listbox.style.bottom = `${window.innerHeight - boundingRect.y}px`;
                    this.listbox.style.left = `${boundingRect.x}px`;
                }
                else {
                    if (this.listbox.style.bottom) {
                        this.listbox.style.removeProperty('bottom');
                    }
                    this.listbox.style.top = `${boundingRect.y + boundingRect.height}px`;
                    this.listbox.style.left = `${boundingRect.x}px`;
                }
                const dialogBoundingRect = (_a = this.parentDialogContent) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (dialogBoundingRect &&
                    (dialogBoundingRect.bottom <= boundingRect.bottom || dialogBoundingRect.top >= boundingRect.top)) {
                    this.open = false;
                }
            }
        });
        this.labelHtmlElement = document.createElement('chameleon-typography');
        this.labelHtmlElement.setAttribute('variant', 'button-small');
        this.labelHtmlElement.classList.add('label');
        this.helperTextHtmlElement = document.createElement('chameleon-typography');
        this.helperTextHtmlElement.setAttribute('variant', 'caption-medium');
        this.helperTextHtmlElement.setAttribute('color', helperTextDefaultColor);
    }
    inDialogChanged(_oldValue, _newValue) {
        if (this.inDialog && !this.tearDownInDialog) {
            this.tearDownInDialog = this.setUpInDialog();
        }
        if (!this.inDialog && this.tearDownInDialog) {
            this.tearDownInDialog();
            this.tearDownInDialog = undefined;
        }
    }
    selectedValueChanged(_oldValue, newValue) {
        this.value = newValue;
    }
    labelChanged(_oldValue, newValue) {
        this.labelHtmlElement.innerHTML = newValue;
    }
    helperTextChanged(_oldValue, newValue) {
        this.helperTextHtmlElement.innerHTML = newValue;
    }
    positionChanged(_oldValue, newValue) {
        super.positionChanged(_oldValue, newValue);
        if (this.inDialog) {
            this.positionListbox();
        }
    }
    successChanged(_oldValue, newValue) {
        if (newValue) {
            this.helperTextHtmlElement.setAttribute('color', 'text-success-01');
        }
        else {
            this.helperTextHtmlElement.removeAttribute('color');
        }
    }
    errorChanged(_oldValue, newValue) {
        if (newValue) {
            this.helperTextHtmlElement.setAttribute('color', 'danger-01');
        }
        else {
            this.helperTextHtmlElement.removeAttribute('color');
        }
    }
    disabledChanged(_oldValue, newValue) {
        var _a, _b, _c, _d;
        super.disabledChanged(_oldValue, newValue);
        if (newValue) {
            (_a = this.helperTextHtmlElement) === null || _a === void 0 ? void 0 : _a.setAttribute('color', 'text-03');
            (_b = this.labelHtmlElement) === null || _b === void 0 ? void 0 : _b.setAttribute('color', 'text-03');
        }
        else {
            (_c = this.helperTextHtmlElement) === null || _c === void 0 ? void 0 : _c.setAttribute('color', helperTextDefaultColor);
            (_d = this.labelHtmlElement) === null || _d === void 0 ? void 0 : _d.setAttribute('color', 'text-01');
        }
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this.querySelector('[slot="indicator"]')) {
            this.createIndicator();
        }
        this.insertLabel();
        this.insertHelperText();
        this.setInitialSelectedValue();
        if (this.inDialog && !this.tearDownInDialog) {
            this.tearDownInDialog = this.setUpInDialog();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.labelHtmlElement.remove();
        this.helperTextHtmlElement.remove();
        if (this.tearDownInDialog) {
            this.tearDownInDialog();
        }
    }
    setUpInDialog() {
        var _a;
        this.parentDialogContent = this.findParentDialogContent();
        if (this.parentDialogContent) {
            this.positionListbox();
            (_a = this.parentDialogContent) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', this.positionListbox);
            window.addEventListener('resize', this.positionListbox);
            return () => {
                var _a;
                this.resetListbox();
                (_a = this.parentDialogContent) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', this.positionListbox);
                window.removeEventListener('resize', this.positionListbox);
            };
        }
    }
    resetListbox() {
        this.listbox.style.removeProperty('position');
        this.listbox.style.removeProperty('width');
        this.listbox.style.removeProperty('top');
        this.listbox.style.removeProperty('left');
    }
    createIndicator() {
        const indicator = document.createElement('chameleon-svg');
        indicator.setAttribute('slot', 'indicator');
        indicator.innerHTML = SVG_CHEVRON_DOWN_OUTLINED;
        this.appendChild(indicator);
    }
    insertLabel() {
        var _a;
        if (this.label) {
            (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.prepend(this.labelHtmlElement);
        }
    }
    insertHelperText() {
        var _a;
        if (this.helperText) {
            (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.append(this.helperTextHtmlElement);
        }
    }
    setInitialSelectedValue() {
        const selectedValue = this.getAttribute('selected-value');
        if (selectedValue !== null) {
            // because fast wait a frame before querying options
            DOM.queueUpdate(() => {
                this.value = selectedValue;
            });
        }
        else {
            this.selectedValue = this.value;
        }
    }
    findParentDialogContent() {
        const dialog = findParent(this, (parent) => parent.tagName === 'CHAMELEON-DIALOG');
        const dialogContent = (dialog === null || dialog === void 0 ? void 0 : dialog.shadowRoot) && findChild(dialog === null || dialog === void 0 ? void 0 : dialog.shadowRoot, (child) => child.part.contains('body'));
        return dialogContent;
    }
}
__decorate([
    attr({ attribute: 'selected-value' })
], SelectComponent.prototype, "selectedValue", void 0);
__decorate([
    attr({ mode: 'boolean' })
], SelectComponent.prototype, "success", void 0);
__decorate([
    attr({ mode: 'boolean' })
], SelectComponent.prototype, "error", void 0);
__decorate([
    attr({ mode: 'boolean' })
], SelectComponent.prototype, "inline", void 0);
__decorate([
    attr
], SelectComponent.prototype, "label", void 0);
__decorate([
    attr({ attribute: 'helper-text' })
], SelectComponent.prototype, "helperText", void 0);
__decorate([
    attr({ mode: 'boolean' })
], SelectComponent.prototype, "fullwidth", void 0);
__decorate([
    attr({ attribute: 'no-border', mode: 'boolean' })
], SelectComponent.prototype, "noBorder", void 0);
__decorate([
    attr({ attribute: 'in-dialog', mode: 'boolean' })
], SelectComponent.prototype, "inDialog", void 0);
